import { ApolloError } from "@apollo/client";
import {
  Box,
  Checkbox,
  Divider,
  Grid,
  MenuItem,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import { useCallback, useEffect, useMemo, useState } from "react";

import StyledDialog from "../../../common/components/dialog/StyledDialog";
import DeviceTemplateNodeSelect from "../../../common/components/layout/components/DeviceTemplateNodeSelect";
import SKUSelect from "../../../common/components/layout/components/SKUSelect";
import { useGetNodesWithoutDeviceByCustomer } from "../../../common/components/select/NodeSelect/useGetNodesWithoutDeviceByCustomer";
import confirmDialogStyleOptions from "../../../common/helpers/confirmDialogParams";
import { useCustomerIdGuard } from "../../../common/hooks/useCustomerIdGuard";
import { useGetCustomer } from "../../../common/hooks/useGetCustomer";
import { checkDuplicateDeviceNames } from "../../../common/utils/checkDuplicateDeviceNames";
import {
  DeviceTypeEnum,
  mapTemplateDataToDeviceInput,
} from "../../../common/utils/mapTemplateDataToDeviceInput";
import { mapTemplateDataToServiceInput } from "../../../common/utils/mapTemplateDataToServiceInput";
import { validateCreateDevicesFromTemplate } from "../../../common/utils/validateCreateDevicesFromTemplate";
import { DEFAULT_DEVICE_PASSWORD } from "../../../common/variables/common";
import {
  errorNotification,
  successNotification,
} from "../../../common/variables/notification";
import { useSelectedLocationDropdown } from "../../../common/variables/selectedLocationDropdown";
import { useGetDevicesByLocation } from "../../customer-settings/tabs/locations/hooks/useGetDevicesByLocation";
import { useSyncShadow } from "../../model-manager/hooks/useSyncShadow";
import { useGetDevicesMakesCached } from "../../system-settings/tabs/devices/hooks/useGetDevicesMakesCached";
import {
  modelLabelMap,
  panTiltTypeOptions,
} from "../../system-settings/tabs/skus/variables/skuData";
import { useCreateBatchDevices } from "../hooks/useCreateBatchDevices";
import { useCreateBatchServices } from "../hooks/useCreateBatchServices";

export type SKUDevice = {
  deviceType: string;
  deviceIp?: string; // Now optional - entered at onboarding time
  model: string[];
  nodeNumber?: number; // Which node this device belongs to (1-based)
  password?: string;
  isPT?: boolean;
  panTiltType?: string;
  hasRecordVideo?: boolean;
};

interface CreateDevicesFromSKUDialogProps {
  open: boolean;
  handleClose: () => void;
}

const CreateDevicesFromSKUDialog = ({
  open,
  handleClose,
}: CreateDevicesFromSKUDialogProps) => {
  const theme = useTheme();
  const confirm = useConfirm();
  const selectedCustomerId = useCustomerIdGuard();
  const selectedLocation = useSelectedLocationDropdown();
  const { data } = useGetNodesWithoutDeviceByCustomer();
  const { data: currentDeviceData, refetchDevices } = useGetDevicesByLocation(
    selectedLocation?.value ?? ""
  );
  const { data: deviceMakesData } = useGetDevicesMakesCached();
  const { customer } = useGetCustomer();

  const { syncShadowsForNode } = useSyncShadow();

  const { batchCreateDevices, loading } = useCreateBatchDevices();

  const { batchCreateServices } = useCreateBatchServices();

  const [hasSubmitted, setHasSubmitted] = useState(false);

  const [devicePassword, setDevicePassword] = useState("");

  const [serialNumber, setSerialNumber] = useState("");

  const [selectedSKU, setSelectedSKU] = useState<{
    id: string;
    skuNumber: string;
    configurationName: string;
    category: string;
    skuDataConfigurations: string;
    nodeCount: number;
    defaultIp?: string | null;
  } | null>(null);

  const [parsedSKUData, setParsedSKUData] = useState<SKUDevice[] | null>(null);

  const [selectedNodes, setSelectedNodes] = useState<
    { nodeId: string; nodeIP: string; nodeName: string }[]
  >([]);

  const [componentSerialNumbers, setComponentSerialNumbers] = useState<
    string[]
  >([]);

  const [deviceIpAddresses, setDeviceIpAddresses] = useState<string[]>([]);

  // PT settings per device (PT at onboarding level)
  const [devicePTEnabled, setDevicePTEnabled] = useState<boolean[]>([]);
  const [devicePTTypes, setDevicePTTypes] = useState<string[]>([]);
  const [devicePTIPs, setDevicePTIPs] = useState<string[]>([]);

  // Models per device (editable at onboarding)
  const [deviceModels, setDeviceModels] = useState<string[][]>([]);

  // Custom name per device (editable at onboarding, pre-filled with auto-generated default)
  const [deviceNames, setDeviceNames] = useState<string[]>([]);

  // Auto-fill password from customer settings (with fallback to default)
  useEffect(() => {
    if (!devicePassword) {
      setDevicePassword(customer?.devicePassword || DEFAULT_DEVICE_PASSWORD);
    }
  }, [customer?.devicePassword]);

  // Check if a device type supports Pan/Tilt
  const getHasPanTiltForDeviceType = useCallback(
    (deviceType: string): boolean => {
      const matched = deviceMakesData?.getDevicesMakes?.items?.find(
        d => d?.model === deviceType
      );

      return matched?.hasPanTilt ?? false;
    },
    [deviceMakesData]
  );

  // Get available models for a device type
  const getModelsForDeviceType = useCallback(
    (deviceType: string): string[] => {
      const matched = deviceMakesData?.getDevicesMakes?.items?.find(
        d => d?.model === deviceType
      );
      const supportedModels = Object.keys(modelLabelMap);

      return (matched?.modelsToRun ?? []).filter(
        (model): model is string =>
          model !== null && supportedModels.includes(model)
      );
    },
    [deviceMakesData]
  );

  const getDeviceTypeSuffix = (deviceType: string): string => {
    const suffixMap: Record<string, string> = {
      [DeviceTypeEnum.CH4]: "ch4",
      [DeviceTypeEnum.V1]: "multi",
      [DeviceTypeEnum.FB4]: "fb4",
      [DeviceTypeEnum.FB4_THERMAL]: "fb4therm",
    };

    return (
      suffixMap[deviceType] ?? deviceType.replace(/\s+/g, "").toLowerCase()
    );
  };

  const generateDefaultDeviceNames = (
    devices: SKUDevice[],
    locationTitle: string
  ): string[] => {
    const cleanLocation = (locationTitle || "").replace(/\s+/g, "");
    const counts: Record<string, number> = {};

    return devices.map(device => {
      const suffix = getDeviceTypeSuffix(device.deviceType);

      counts[suffix] = (counts[suffix] || 0) + 1;

      return `${cleanLocation}-${suffix}-${counts[suffix]}`;
    });
  };

  const resetDialogState = () => {
    setHasSubmitted(false);

    setDevicePassword("");

    setSerialNumber("");

    setSelectedSKU(null);

    setParsedSKUData(null);

    setSelectedNodes([]);

    setComponentSerialNumbers([]);

    setDeviceIpAddresses([]);

    setDevicePTEnabled([]);

    setDevicePTTypes([]);

    setDevicePTIPs([]);

    setDeviceModels([]);

    setDeviceNames([]);
  };

  const closeDialog = (): void => {
    handleClose();

    resetDialogState();
  };

  const confirmDialogClose = (): void => {
    confirm({
      title: "Are you sure you want to cancel?",
      confirmationText: "Cancel",
      cancellationText: "Back",
      ...confirmDialogStyleOptions,
      confirmationButtonProps: {
        ...confirmDialogStyleOptions.confirmationButtonProps,
        color: "primary",
      },
    })
      .then(async (): Promise<void> => {
        closeDialog();
      })
      .catch(() => {
        // User clicked "Back" - do nothing, keep dialog open
      });
  };

  const existingNames =
    currentDeviceData?.getDevicesByLocation?.items
      ?.map(d => d?.id?.replace(/^DE#/, "").toLowerCase())
      .filter((id): id is string => !!id) ?? [];

  const filteredNodes = useMemo(() => {
    if (!data) return [];

    return data.getNodes?.items
      ?.filter(node => node?.locationId === selectedLocation?.value)
      ?.map(node => ({
        nodeId:
          node?.nodeId
            ?.replace("C_", "C#")
            .replace("_L_", "#L#")
            .replace("_N_", "#N#") ?? "",
        nodeIP: node?.nodeIP ?? "",
        nodeName: node?.nodeName ?? "",
      }))
      .filter(n => n.nodeId && n.nodeIP && n.nodeName);
  }, [data, selectedLocation?.value]);

  const saveDevice = async (): Promise<void> => {
    setHasSubmitted(true);

    const validation = validateCreateDevicesFromTemplate({
      parsedTemplateData: parsedSKUData,
      selectedNodes,
      devicePassword,
    });

    if (!validation.isValid) {
      errorNotification(validation.errorMessage!);

      return;
    }

    // Password strength validation: if provided, must be at least 8 characters
    if (devicePassword.length > 0 && devicePassword.length < 8) {
      errorNotification("Password must be at least 8 characters");

      return;
    }

    // Validate IP addresses are provided
    const missingIps = deviceIpAddresses.some(ip => !ip.trim());

    if (missingIps) {
      errorNotification("All device IP addresses are required");

      return;
    }

    // Validate each device has at least one model selected
    for (let i = 0; i < deviceModels.length; i++) {
      const models = deviceModels[i] || [];

      if (models.length === 0) {
        errorNotification(
          `Device ${i + 1}: At least one model must be selected`
        );

        return;
      }
    }

    // Validate device names: only letters, numbers, and hyphens allowed
    const deviceNamePattern = /^[A-Za-z0-9-]+$/;

    for (let i = 0; i < deviceNames.length; i++) {
      const name = deviceNames[i]?.trim();

      if (name && !deviceNamePattern.test(name)) {
        errorNotification(
          `Device ${i + 1}: Name can only contain letters, numbers, and hyphens`
        );

        return;
      }
    }

    // Validate PT IP is provided when PT is enabled
    for (let i = 0; i < devicePTEnabled.length; i++) {
      if (devicePTEnabled[i] && !devicePTIPs[i]?.trim()) {
        errorNotification(
          `Device ${i + 1}: PT IP is required when PT is enabled`
        );

        return;
      }
    }

    try {
      // Merge onboarding-time data (IPs, PT settings, models) into the template data
      const templateDataWithOnboardingSettings = parsedSKUData!.map(
        (device, index) => {
          const isPT = devicePTEnabled[index] ?? false;
          const deviceIP = deviceIpAddresses[index] || "";
          const ptIP = devicePTIPs[index] || deviceIP;
          const ptType = devicePTTypes[index] || "";

          // Fallback auto-assign if PT type is somehow empty
          const finalPTType =
            isPT && !ptType
              ? ptIP === deviceIP
                ? "CH4-UCv2"
                : "JSys"
              : ptType;

          return {
            ...device,
            deviceIp: deviceIP,
            isPT,
            panTiltIP: ptIP,
            panTiltType: finalPTType,
            model: deviceModels[index] || device.model || [],
          };
        }
      );

      const devicesToCreate = mapTemplateDataToDeviceInput({
        templateData: templateDataWithOnboardingSettings,
        selectedCustomerId,
        selectedLocation: selectedLocation ?? { title: "", value: "" },
        selectedNodes,
        devicePassword,
        skuNumber: selectedSKU?.skuNumber,
        configurationName: selectedSKU?.configurationName,
        serialNumber: serialNumber || undefined,
        componentSerialNumbers,
      });

      // Apply user-customized names (fall back to auto-generated if field is empty)
      const devicesWithCustomNames = devicesToCreate.map((device, index) => ({
        ...device,
        name: deviceNames[index]?.trim() || device.name,
      }));

      const uniqueDevices = checkDuplicateDeviceNames(
        existingNames,
        devicesWithCustomNames
      );

      const servicesToCreate = mapTemplateDataToServiceInput(
        uniqueDevices,
        templateDataWithOnboardingSettings
      );

      await batchCreateDevices(uniqueDevices);

      const serviceResponse = await batchCreateServices(servicesToCreate);

      if (serviceResponse?.errors) {
        errorNotification("Failed to create services");

        return;
      }

      closeDialog();

      successNotification("Devices created from SKU Configuration!");

      try {
        // Sync shadows for all selected nodes
        await Promise.all(
          selectedNodes.map(async node => {
            if (node?.nodeId) {
              // nodeId is already in correct # format from filteredNodes
              await syncShadowsForNode(node.nodeId);
            }
          })
        );
      } catch (error) {
        console.error(error);
      }
    } catch (error) {
      if (
        (error as ApolloError).message.includes(
          "The conditional request failed"
        )
      ) {
        errorNotification("Device ID already exists");
      } else {
        errorNotification((error as ApolloError).message);
      }
    }
  };

  const handleSKUChange = ({
    id,
    skuNumber,
    configurationName,
    category,
    skuDataConfigurations,
    nodeCount,
    defaultIp,
  }: {
    id: string;
    skuNumber: string;
    configurationName: string;
    category: string;
    skuDataConfigurations: string;
    nodeCount: number;
    defaultIp?: string | null;
  }) => {
    try {
      const parsedData = JSON.parse(skuDataConfigurations);

      setSelectedSKU({
        id,
        skuNumber,
        configurationName,
        category,
        skuDataConfigurations,
        nodeCount,
      });

      setParsedSKUData(parsedData);

      // Initialize component serial numbers array with empty strings for each device
      setComponentSerialNumbers(Array(parsedData.length).fill(""));

      // Initialize device IP addresses: pre-fill with SKU's defaultIp if set
      const prefillIp = (defaultIp ?? "").replaceAll(" ", "");

      setDeviceIpAddresses(Array(parsedData.length).fill(prefillIp));

      // Set password from SKU if defined (use first device's password as SKU default)
      // Per AC#3: "If a SKU has a default password: it is applied to devices created using that SKU"
      const skuPassword = parsedData[0]?.password || "";

      setDevicePassword(skuPassword);

      // Initialize PT enabled based on device type capability
      setDevicePTEnabled(
        parsedData.map((d: SKUDevice) =>
          getHasPanTiltForDeviceType(d.deviceType)
        )
      );

      // Initialize PT IPs - empty, user must enter
      setDevicePTIPs(Array(parsedData.length).fill(""));

      // Initialize PT types - empty, will be auto-determined when user enters IPs
      setDevicePTTypes(Array(parsedData.length).fill(""));

      // Initialize models from SKU data (editable at onboarding)
      setDeviceModels(parsedData.map((d: SKUDevice) => d.model || []));

      // Initialize device names with auto-generated defaults (user can customize)
      setDeviceNames(
        generateDefaultDeviceNames(parsedData, selectedLocation?.title ?? "")
      );

      refetchDevices(selectedLocation?.value ?? "");

      // Initialize selectedNodes array - one entry per device for per-device node assignment
      setSelectedNodes(
        Array(parsedData.length).fill({
          nodeId: "",
          nodeIP: "",
          nodeName: "",
        })
      );

      setHasSubmitted(false);
    } catch (error) {
      errorNotification("Invalid SKU Configuration data format");
    }
  };

  const handleDeviceNameChange = (index: number, value: string) => {
    setDeviceNames(prev => {
      const updated = [...prev];

      updated[index] = value;

      return updated;
    });
  };

  const handleComponentSerialNumberChange = (index: number, value: string) => {
    setComponentSerialNumbers(prev => {
      const updated = [...prev];

      updated[index] = value;

      return updated;
    });
  };

  const handleDeviceIpChange = (index: number, value: string) => {
    const cleanedIP = value.replaceAll(" ", "");

    setDeviceIpAddresses(prev => {
      const updated = [...prev];

      updated[index] = cleanedIP;

      return updated;
    });

    // Re-evaluate PT type if PT is enabled and PT IP is set
    const ptEnabled = devicePTEnabled[index];
    const currentPTIP = devicePTIPs[index];

    if (ptEnabled && currentPTIP) {
      setDevicePTTypes(prev => {
        const updated = [...prev];

        updated[index] = determineAutoAssignedPTType(cleanedIP, currentPTIP);

        return updated;
      });
    }
  };

  const handleNodeSelect = (
    index: number,
    node: { nodeId: string; nodeIP: string; nodeName: string }
  ) => {
    const updated = [...selectedNodes];

    updated[index] = node;

    setSelectedNodes(updated);
  };

  // Auto-determine PT type based on IP comparison
  // Logic from updateNamedShadows.js: panTiltIP === cameraIP ? "CH4-UCv2" : "JSys"
  const determineAutoAssignedPTType = (
    deviceIP: string,
    ptIP: string
  ): string => {
    // If PT IP is empty or matches device IP, it's integrated (CH4-UCv2)
    // If PT IP is different, it's a separate PT unit (JSys)
    if (!ptIP || ptIP === deviceIP) {
      return "CH4-UCv2";
    }

    return "JSys";
  };

  // Handle PT enabled toggle
  const handlePTEnabledChange = (index: number, enabled: boolean) => {
    setDevicePTEnabled(prev => {
      const updated = [...prev];

      updated[index] = enabled;

      return updated;
    });

    if (!enabled) {
      // Clear PT IP and type when PT is disabled
      setDevicePTIPs(prev => {
        const updated = [...prev];

        updated[index] = "";

        return updated;
      });

      setDevicePTTypes(prev => {
        const updated = [...prev];

        updated[index] = "";

        return updated;
      });
    }
    // When enabled: PT IP starts empty, user must enter it
    // PT Type will be auto-determined when user enters PT IP
  };

  // Handle PT IP change - auto-determine PT type based on comparison
  const handlePTIPChange = (index: number, ptIP: string) => {
    const cleanedIP = ptIP.replaceAll(" ", "");

    setDevicePTIPs(prev => {
      const updated = [...prev];

      updated[index] = cleanedIP;

      return updated;
    });

    // Auto-determine PT type based on IP comparison
    const deviceIP = deviceIpAddresses[index] || "";

    setDevicePTTypes(prev => {
      const updated = [...prev];

      updated[index] = determineAutoAssignedPTType(deviceIP, cleanedIP);

      return updated;
    });
  };

  // Handle PT type change (manual override)
  const handlePTTypeChange = (index: number, ptType: string) => {
    setDevicePTTypes(prev => {
      const updated = [...prev];

      updated[index] = ptType;

      return updated;
    });
  };

  // Get unique nodes already selected (for limiting selection to nodeCount)
  const getUniqueSelectedNodeIds = (): Set<string> => {
    return new Set(selectedNodes.filter(n => n.nodeId).map(n => n.nodeId));
  };

  // Filter nodes for each device dropdown - only allow nodes already selected
  // or new nodes if we haven't reached the nodeCount limit
  const getFilteredNodesForDevice = (deviceIndex: number) => {
    const uniqueSelectedIds = getUniqueSelectedNodeIds();
    const maxNodes = selectedSKU?.nodeCount ?? 1;
    const currentDeviceNodeId = selectedNodes[deviceIndex]?.nodeId;

    return filteredNodes.filter(node => {
      // Always allow the node currently selected for this device
      if (node.nodeId === currentDeviceNodeId) return true;

      // Allow nodes that are already selected by other devices
      if (uniqueSelectedIds.has(node.nodeId)) return true;

      // Allow new nodes only if we haven't reached the limit
      return uniqueSelectedIds.size < maxNodes;
    });
  };

  return (
    <StyledDialog
      open={open}
      title="Create new device(s) from SKU configuration"
      onClose={confirmDialogClose}
      SubmitButtonProps={{
        loading,
        onSubmit: saveDevice,
      }}
    >
      <Grid container justifyContent="center" direction="column" spacing={2}>
        <Grid item>
          <Typography variant="body1">Location</Typography>
          <Box
            sx={{
              color: theme.palette.text.primary,
              fontWeight: "bold",
            }}
          >
            {selectedLocation?.title}
          </Box>
        </Grid>
        <Grid item>
          <SKUSelect
            skuId={selectedSKU?.skuNumber ?? ""}
            onSKUChange={handleSKUChange}
            error={hasSubmitted && !selectedSKU?.skuNumber}
            helperText={
              hasSubmitted && !selectedSKU?.skuNumber
                ? "SKU selection is required"
                : ""
            }
          />
        </Grid>
        <Grid item>
          <TextField
            fullWidth
            label="Serial Number (S/N)"
            value={serialNumber}
            onChange={e => setSerialNumber(e.target.value)}
            helperText="Unit serial number for this product (optional)"
          />
        </Grid>
        <Grid item>
          <Typography variant="body1">Device Password</Typography>
          <Box mt={1}>
            <TextField
              fullWidth
              variant="outlined"
              value={devicePassword}
              onChange={e => setDevicePassword(e.target.value)}
              placeholder="Camera credentials (optional)"
              error={devicePassword.length > 0 && devicePassword.length < 8}
              helperText={
                devicePassword.length > 0 && devicePassword.length < 8
                  ? "Password must be at least 8 characters"
                  : "Can be edited per device after onboarding"
              }
            />
          </Box>
        </Grid>

        {selectedSKU && parsedSKUData && parsedSKUData.length > 0 && (
          <>
            <Grid item>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Assign Devices to Nodes
              </Typography>
              <Box sx={{ width: "100%" }}>
                {parsedSKUData.map((device, index) => {
                  const deviceSupportsPT = getHasPanTiltForDeviceType(
                    device.deviceType
                  );
                  const ptEnabled = devicePTEnabled[index] ?? false;
                  const availableModels = getModelsForDeviceType(
                    device.deviceType
                  );
                  const selectedModels = deviceModels[index] || [];

                  return (
                    <Box
                      key={index}
                      sx={{
                        mb: 2,
                        pb: 2,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      {/* Row 1: Device name header with divider */}
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Device {index + 1}: {device.deviceType}
                      </Typography>
                      <Divider
                        sx={{
                          mb: 1.5,
                          mt: 0.5,
                          borderBottomWidth: 2,
                          borderColor: "primary.main",
                        }}
                      />
                      {/* Row 2: Device Name */}
                      <Box sx={{ mb: 1 }}>
                        <TextField
                          size="small"
                          label="Device Name"
                          value={deviceNames[index] ?? ""}
                          onChange={e =>
                            handleDeviceNameChange(index, e.target.value)
                          }
                          error={
                            hasSubmitted &&
                            !!deviceNames[index]?.trim() &&
                            !/^[A-Za-z0-9-]+$/.test(deviceNames[index].trim())
                          }
                          helperText="Letters, numbers, and hyphens only"
                          fullWidth
                        />
                      </Box>
                      {/* Row 3: Device IP */}
                      <Box sx={{ mb: 1 }}>
                        <TextField
                          size="small"
                          label="Device IP"
                          value={deviceIpAddresses[index] || ""}
                          onChange={e =>
                            handleDeviceIpChange(index, e.target.value)
                          }
                          error={
                            hasSubmitted && !deviceIpAddresses[index]?.trim()
                          }
                          placeholder="192.168.1.x"
                          fullWidth
                        />
                      </Box>
                      {/* Row 3: Node Select */}
                      <Box sx={{ mb: 1 }}>
                        <DeviceTemplateNodeSelect
                          index={index}
                          selectedNodeId={selectedNodes[index]?.nodeId ?? ""}
                          filteredNodes={getFilteredNodesForDevice(index)}
                          onChange={node => handleNodeSelect(index, node)}
                          error={hasSubmitted && !selectedNodes[index]?.nodeId}
                          helperText={
                            hasSubmitted && !selectedNodes[index]?.nodeId
                              ? "Node is required"
                              : ""
                          }
                          size="small"
                          fullWidth
                        />
                      </Box>
                      {/* Row 4: Component S/N */}
                      <Box sx={{ mb: 1 }}>
                        <TextField
                          size="small"
                          label="Component S/N"
                          value={componentSerialNumbers[index] || ""}
                          onChange={e =>
                            handleComponentSerialNumberChange(
                              index,
                              e.target.value
                            )
                          }
                          placeholder="Optional"
                          fullWidth
                        />
                      </Box>
                      {/* Row 2: PT dropdown (if device supports PT) */}
                      {deviceSupportsPT && (
                        <Box sx={{ mb: 1 }}>
                          <TextField
                            select
                            size="small"
                            label="Pan Tilt"
                            value={ptEnabled ? "true" : "false"}
                            onChange={e =>
                              handlePTEnabledChange(
                                index,
                                e.target.value === "true"
                              )
                            }
                            fullWidth
                          >
                            <MenuItem value="true">Yes</MenuItem>
                            <MenuItem value="false">No</MenuItem>
                          </TextField>
                        </Box>
                      )}
                      {/* Row 2b: PT IP and PT Type (if PT is enabled) */}
                      {deviceSupportsPT && ptEnabled && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            mb: 1,
                          }}
                        >
                          <TextField
                            size="small"
                            label="Pan Tilt IP"
                            value={devicePTIPs[index] || ""}
                            onChange={e =>
                              handlePTIPChange(index, e.target.value)
                            }
                            placeholder="192.168.1.x"
                            sx={{ minWidth: 130 }}
                            error={
                              hasSubmitted &&
                              ptEnabled &&
                              !devicePTIPs[index]?.trim()
                            }
                            helperText={
                              hasSubmitted &&
                              ptEnabled &&
                              !devicePTIPs[index]?.trim()
                                ? "Pan Tilt IP required"
                                : ""
                            }
                          />
                          <TextField
                            select
                            size="small"
                            label="Pan Tilt Type"
                            value={devicePTTypes[index] || ""}
                            onChange={e =>
                              handlePTTypeChange(index, e.target.value)
                            }
                            sx={{ minWidth: 150 }}
                          >
                            <MenuItem value="">
                              <em>Select PT Type</em>
                            </MenuItem>
                            {panTiltTypeOptions.map(option => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Box>
                      )}
                      {/* Row 3: Models */}
                      {availableModels.length > 0 && (
                        <Box>
                          <TextField
                            select
                            label="Model(s)"
                            size="small"
                            value={selectedModels}
                            onChange={e => {
                              const value = e.target
                                .value as unknown as string[];

                              setDeviceModels(prev => {
                                const updated = [...prev];

                                updated[index] = value.filter(
                                  v => v.trim() !== ""
                                );

                                return updated;
                              });
                            }}
                            SelectProps={{
                              multiple: true,
                              renderValue: selected =>
                                (selected as string[])
                                  .filter(model => model.trim() !== "")
                                  .map(model => modelLabelMap[model] || model)
                                  .join(", "),
                            }}
                            sx={{ minWidth: 220 }}
                          >
                            {availableModels.map(model => (
                              <MenuItem key={model} value={model}>
                                <Checkbox
                                  checked={selectedModels.includes(model)}
                                  sx={{ marginRight: 1 }}
                                />
                                {modelLabelMap[model] || model}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Grid>
          </>
        )}
      </Grid>
    </StyledDialog>
  );
};

export default CreateDevicesFromSKUDialog;
